/**
 * Animation Utilities for MyMinu
 * 
 * Centralized animation configuration using Framer Motion.
 * All animations follow the "fluid motion of time" design philosophy.
 */

import type { Variants, Transition } from 'framer-motion';

// =============================================================================
// EASING FUNCTIONS
// =============================================================================

/**
 * Easing configurations for consistent motion feel
 */
export const EASING = {
  /** Smooth deceleration - used for drawing the MinuLine */
  drawLine: [0.33, 1, 0.68, 1] as const,
  
  /** Standard Material Design easing */
  standard: [0.4, 0, 0.2, 1] as const,
  
  /** Quick ease out for snappy interactions */
  snappy: [0.25, 0.1, 0.25, 1] as const,
  
  /** Gentle ease for subtle movements */
  gentle: [0.4, 0, 0.6, 1] as const,
};

/**
 * Spring configurations for natural, bouncy motion
 */
export const SPRINGS = {
  /** Low bounce, high damping - for settling animations */
  settle: { type: 'spring' as const, stiffness: 300, damping: 30 },
  
  /** Gentle spring for the MinuLine hand */
  fluid: { type: 'spring' as const, stiffness: 50, damping: 15 },
  
  /** Snappy spring for button presses */
  snappy: { type: 'spring' as const, stiffness: 400, damping: 25 },
  
  /** Soft spring for modals and overlays */
  soft: { type: 'spring' as const, stiffness: 200, damping: 20 },
};

// =============================================================================
// TIMING CONSTANTS
// =============================================================================

export const DURATIONS = {
  /** Standard greeting animation */
  greeting: 1.0,
  
  /** Fast greeting for return visits */
  greetingFast: 0.4,
  
  /** MinuLine draw animation */
  draw: 0.8,
  
  /** Quick micro-interactions */
  micro: 0.15,
  
  /** Standard transitions */
  standard: 0.3,
  
  /** Slow, ambient animations */
  ambient: 2.0,
};

// =============================================================================
// REUSABLE VARIANTS
// =============================================================================

/**
 * Page transition variants - slide + fade
 */
export const pageTransition: Variants = {
  initial: { 
    opacity: 0, 
    x: 20,
  },
  animate: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: DURATIONS.standard,
      ease: EASING.standard,
    },
  },
  exit: { 
    opacity: 0, 
    x: -20,
    transition: {
      duration: DURATIONS.micro,
      ease: EASING.snappy,
    },
  },
};

/**
 * Fade in with upward motion
 */
export const fadeInUp: Variants = {
  initial: { 
    opacity: 0, 
    y: 20,
  },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: DURATIONS.standard,
      ease: EASING.standard,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: DURATIONS.micro,
    },
  },
};

/**
 * Simple fade
 */
export const fade: Variants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: { duration: DURATIONS.standard },
  },
  exit: { 
    opacity: 0,
    transition: { duration: DURATIONS.micro },
  },
};

/**
 * Scale in from center (for modals, popups)
 */
export const scaleIn: Variants = {
  initial: { 
    opacity: 0, 
    scale: 0.9,
  },
  animate: { 
    opacity: 1, 
    scale: 1,
    transition: SPRINGS.soft,
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    transition: { duration: DURATIONS.micro },
  },
};

/**
 * Slide up from bottom (for drawers, bottom sheets)
 */
export const slideUp: Variants = {
  initial: { 
    y: '100%',
    opacity: 0,
  },
  animate: { 
    y: 0,
    opacity: 1,
    transition: SPRINGS.soft,
  },
  exit: { 
    y: '100%',
    opacity: 0,
    transition: { 
      duration: DURATIONS.standard,
      ease: EASING.standard,
    },
  },
};

/**
 * Container for staggered children animations
 */
export const staggerContainer: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.02,
      staggerDirection: -1,
    },
  },
};

/**
 * Child item for stagger animations
 */
export const staggerItem: Variants = {
  initial: { 
    opacity: 0, 
    y: 10,
  },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: DURATIONS.standard,
      ease: EASING.standard,
    },
  },
  exit: {
    opacity: 0,
    y: -5,
    transition: { duration: DURATIONS.micro },
  },
};

/**
 * Subtle pulse animation (for active tasks)
 */
export const pulse: Variants = {
  initial: { scale: 1 },
  animate: {
    scale: [1, 1.03, 1],
    transition: {
      duration: 0.6,
      ease: EASING.gentle,
    },
  },
};

/**
 * Task wedge entry animation
 */
export const wedgeEntry: Variants = {
  initial: { 
    scale: 0.8, 
    opacity: 0,
  },
  animate: { 
    scale: 1, 
    opacity: 1,
    transition: {
      duration: DURATIONS.standard,
      ease: EASING.standard,
    },
  },
  exit: {
    scale: 0.9,
    opacity: 0,
    transition: { duration: DURATIONS.micro },
  },
};

// =============================================================================
// MINULINE SPECIFIC
// =============================================================================

/**
 * MinuLine path drawing animation
 */
export const minuLineDraw: Variants = {
  initial: { 
    pathLength: 0,
    opacity: 0,
  },
  animate: { 
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: {
        duration: DURATIONS.draw,
        ease: EASING.drawLine,
      },
      opacity: {
        duration: 0.1,
      },
    },
  },
  exit: {
    pathLength: 0,
    opacity: 0,
    transition: { duration: DURATIONS.micro },
  },
};

/**
 * MinuLine leading dot animation
 */
export const minuLineDot: Variants = {
  initial: { 
    scale: 0,
    opacity: 0,
  },
  animate: { 
    scale: 1,
    opacity: 1,
    transition: SPRINGS.snappy,
  },
  exit: {
    scale: 0,
    opacity: 0,
    transition: { duration: DURATIONS.micro },
  },
};

/**
 * Greeting text fade in
 */
export const greetingText: Variants = {
  initial: { 
    opacity: 0,
    y: 10,
  },
  animate: { 
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.8,
      duration: 0.4,
      ease: EASING.gentle,
    },
  },
  exit: {
    opacity: 0,
    transition: { duration: DURATIONS.micro },
  },
};

// =============================================================================
// TRANSITION PRESETS
// =============================================================================

/**
 * Standard transition for most animations
 */
export const standardTransition: Transition = {
  duration: DURATIONS.standard,
  ease: EASING.standard,
};

/**
 * Quick transition for micro-interactions
 */
export const quickTransition: Transition = {
  duration: DURATIONS.micro,
  ease: EASING.snappy,
};

// =============================================================================
// COLOR CONSTANTS
// =============================================================================

/**
 * MinuLine color palette - matches logo_lines assets
 */
export const MINULINE_COLORS = {
  /** Primary blue - main decorative color */
  primary: '#6B7FD7',
  
  /** Secondary green - for gradient variations */
  secondary: '#3CB9A0',
  
  /** Accent purple */
  purple: '#A4267D',
  
  /** Accent red */
  red: '#E85D5D',
  
  /** Accent yellow */
  yellow: '#F5C542',
  
  /** Glow color with opacity */
  glow: 'rgba(107, 127, 215, 0.15)',
};

// =============================================================================
// DECORATIVE LINE VARIANTS
// =============================================================================

/**
 * Draw-on animation for decorative lines
 */
export const decorativeLineDraw: Variants = {
  initial: {
    pathLength: 0,
    opacity: 0,
  },
  animate: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: {
        duration: DURATIONS.ambient,
        ease: EASING.drawLine,
      },
      opacity: {
        duration: 0.5,
      },
    },
  },
  exit: {
    pathLength: 0,
    opacity: 0,
    transition: {
      duration: DURATIONS.standard,
      ease: EASING.snappy,
    },
  },
};

/**
 * Transition line animation - swoosh across screen
 */
export const transitionLineSwoosh: Variants = {
  initial: {
    pathLength: 0,
    opacity: 0,
  },
  enter: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: {
        duration: 0.6,
        ease: EASING.drawLine,
      },
      opacity: {
        duration: 0.2,
      },
    },
  },
  exit: {
    pathLength: 0,
    opacity: 0,
    transition: {
      pathLength: {
        duration: 0.4,
        ease: EASING.snappy,
      },
      opacity: {
        duration: 0.2,
        delay: 0.2,
      },
    },
  },
};

/**
 * Subtle float animation for ambient decorative lines
 */
export const decorativeLineFloat: Variants = {
  initial: {
    y: 0,
    rotate: 0,
  },
  animate: {
    y: [0, -5, 0],
    rotate: [0, 1, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      repeatType: 'reverse',
      ease: 'easeInOut',
    },
  },
};


